import { Box } from '@mui/material';
import { usePrevious } from 'common';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { DeviceType, useBreakpoint } from '../../hooks/useBreakpoint';
import {
  CfDataGridDataRequest,
  CfDataGridDisplayMode,
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfRecomputeRowHeightsRefCallback,
} from './CfDataGridProps';
import { CfDataGridCardView } from './components/DataGridCardView/CfDataGridCardView';
import { CfDataGridDisplayModeToggle } from './components/DataGridDisplayModeToggle/CfDataGridDisplayModeToggle';
import { CfDataGridFilter } from './components/DataGridFilter/CfDataGridFilter';
import { CfDataGridLoadMore } from './components/DataGridLoadMore/DataGridLoadMore';
import { CfDataGridPagination } from './components/DataGridPagination/CfDataGridPagination';
import { CfDataGridMultiTableView } from './components/DataGridSheetView/CfDataGridSheetView';
import { CfDataGridTableView } from './components/DataGridTableView/CfDataGridTableView';
import {
  getFilterPlaceholderText,
  getFilterText,
  getIsLoading,
  getLastPage,
  getPagingMode,
  getProductKey,
  getTotalItemCount,
  getTotalPageCount,
} from './utility/data-grid-props';
import { KeyboardNavigator } from './utility/keyboard-navigation';

export function CfDataGrid<TItem, TAppState>(
  props: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>
): JSX.Element {
  const breakpointProperties = useBreakpoint();
  const isMobile = breakpointProperties.deviceType === DeviceType.Mobile;
  const filterInputRef = useRef<HTMLInputElement | null>(null);
  const loadMoreRef = useRef<HTMLButtonElement | null>(null);
  const keyboardNavigator = useRef<KeyboardNavigator | null>(null);

  if (keyboardNavigator.current === null) {
    keyboardNavigator.current = new KeyboardNavigator({
      wraparound: props.displayMode === 'card',
      onNavigateOutside: (direction) => {
        if (direction === 'up') {
          filterInputRef.current?.focus();
        }
        if (direction === 'down') {
          if (props.defaultFocusTarget) {
            props.defaultFocusTarget.current?.focus();
          } else {
            loadMoreRef.current?.focus();
          }
        }
      },
      onEnter: () => {
        let handled = false;
        if (props.defaultFocusTarget?.current?.focus) {
          props.defaultFocusTarget?.current?.focus();
          handled = true;
        }
        return handled;
      },
    });
  }

  const triggerOnDataRequested = (newDataRequest: CfDataGridDataRequest) => {
    props.onDataRequested?.(newDataRequest);
    if (props.autoFocus === 'filter' && !isMobile) {
      filterInputRef.current?.focus();
    }
  };

  const isLoading = getIsLoading(props);
  const prevIsLoading = usePrevious(isLoading);

  const displayMode = props.displayMode;
  const itemCount = getTotalItemCount(props);
  const oldFilterText = getFilterText(props);
  const placeholderText = getFilterPlaceholderText(props);
  const pagingMode = getPagingMode(props);
  const totalPageCount = getTotalPageCount(props);
  const lastPage = getLastPage(props);
  const pageSize = lastPage?.pageSize ?? 25;
  const productKey = getProductKey(props);

  const initialRenderComplete = useRef<boolean>(false);

  useEffect(() => {
    if (props.focus) {
      keyboardNavigator.current?.focusElement(props.focus.rowIndex, props.focus.elementIndex);
    }
  }, [, props.focus]);

  useEffect(() => {
    if (isLoading && lastPage?.pageIndex === 0) {
      window.scrollTo(0, 0);
    }
  }, [isLoading]);

  useEffect(() => {
    if (props.autoFocus === 'filter' && !props.focus && !isMobile && isLoading) {
      filterInputRef.current?.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    if (initialRenderComplete.current) initialRenderComplete.current = false;
  }, [props.id]);

  useEffect(() => {
    if (props.autoFocus === 'firstNavigableElement' && !isMobile) {
      if (itemCount <= 0 || isLoading || prevIsLoading === undefined || initialRenderComplete.current) return;
      if (pagingMode === 'load-more' && lastPage?.pageIndex !== 0) return;
      setTimeout(() => {
        keyboardNavigator.current?.firstEnabledElement?.focus();
      }, 0);
      initialRenderComplete.current = true;
    }
  }, [isLoading, displayMode, itemCount, pagingMode, lastPage?.pageIndex]);

  const recomputeTableHeightsRef = useRef<(() => void) | null>(null);
  const recomputeCardHeightsRef = useRef<(() => void) | null>(null);

  const setRecomputeTableHeightsRef: CfRecomputeRowHeightsRefCallback = (recomputeHeights: (() => void) | null) => {
    recomputeTableHeightsRef.current = recomputeHeights;
  };
  const setRecomputeCardHeightsRef: CfRecomputeRowHeightsRefCallback = (recomputeHeights: (() => void) | null) => {
    recomputeCardHeightsRef.current = recomputeHeights;
  };
  const handleDisplayModeChange = (displayMode: CfDataGridDisplayMode) => {
    // allow fresh row heights to be calculated when changing display modes
    recomputeTableHeightsRef.current?.();
    recomputeCardHeightsRef.current?.();

    props.onDisplayModeChanged?.(displayMode);
    initialRenderComplete.current = false;
  };

  const handleFilterChange = (filterText?: string) => {
    triggerOnDataRequested({ filterText, pageSize, pageIndex: 0 });
  };

  const handlePagingChange = (pageIndex: number, pageSize: number) => {
    triggerOnDataRequested({ filterText: oldFilterText, pageIndex, pageSize });
  };

  const handleNextPageRequested = (pageIndex: number) => {
    productKey
      ? triggerOnDataRequested({
          productKey: productKey,
          pageIndex,
          pageSize: pageSize,
        })
      : triggerOnDataRequested({
          filterText: oldFilterText,
          pageIndex,
          pageSize: pageSize,
        });
  };

  const filterInput = (
    <CfDataGridFilter
      keyboardNavigator={keyboardNavigator.current}
      filterText={oldFilterText}
      filterPlaceholderText={placeholderText}
      onChange={handleFilterChange}
      testId={props.testIds?.filter}
      searchButtonTestId={props.searchButtonTestId}
      inputRef={filterInputRef}
      sx={props.filterInputSx}
      isResponsive={props.isFilterInputResponsive}
    />
  );

  let pagination: JSX.Element = <Box />;
  if (pagingMode === 'none') {
    pagination = <Box sx={{ height: 0 }}></Box>;
  } else if (pagingMode === 'pagination' && lastPage?.isLoading === false) {
    pagination = (
      <CfDataGridPagination
        onInit={handlePagingChange}
        onChange={handlePagingChange}
        dataGridProps={props}
        sx={{ width: '100%' }}
      />
    );
  } else if (
    pagingMode === 'load-more' &&
    lastPage &&
    lastPage.isLoading === false &&
    lastPage.pageIndex + 1 < totalPageCount
  ) {
    pagination = (
      <CfDataGridLoadMore
        keyboardNavigator={keyboardNavigator.current}
        pageEndIndex={lastPage?.pageIndex ?? 0}
        onNextPageRequested={handleNextPageRequested}
        breakpointProperties={breakpointProperties}
        buttonRef={loadMoreRef}
      />
    );
  }

  const showDisplayModeToggle = props.cardView && !props.hideDisplayModeToggle;
  const displayModeToggle = (
    <CfDataGridDisplayModeToggle displayMode={props.displayMode ?? 'table'} onChange={handleDisplayModeChange} />
  );

  return (
    <>
      {oldFilterText !== undefined &&
        (props.componentContainers?.filterInputContainer ? (
          ReactDOM.createPortal(filterInput, props.componentContainers.filterInputContainer)
        ) : (
          <Box display='flex' justifyContent='end' sx={{ my: 1 }}>
            <Box sx={{ width: '50%' }}>{filterInput}</Box>
          </Box>
        ))}
      {showDisplayModeToggle &&
        (props.componentContainers?.displayModeToggleContainer ? (
          ReactDOM.createPortal(displayModeToggle, props.componentContainers.displayModeToggleContainer)
        ) : (
          <Box display='flex' justifyContent='end' sx={{ my: 1 }}>
            {displayModeToggle}
          </Box>
        ))}

      {props.displayMode === 'card' ? (
        <CfDataGridCardView
          dataGridProps={props}
          keyboardNavigator={keyboardNavigator.current}
          breakpointProperties={breakpointProperties}
          onNextPageRequested={handleNextPageRequested}
          recomputeRowHeightsRef={setRecomputeCardHeightsRef}
        />
      ) : props.sheetView ? (
        <CfDataGridMultiTableView
          dataGridProps={props}
          keyboardNavigator={keyboardNavigator.current}
          breakpointProperties={breakpointProperties}
        />
      ) : (
        <CfDataGridTableView
          dataGridProps={props}
          keyboardNavigator={keyboardNavigator.current}
          breakpointProperties={breakpointProperties}
          onNextPageRequested={handleNextPageRequested}
          recomputeRowHeightsRef={setRecomputeTableHeightsRef}
        />
      )}
      {props.componentContainers?.paginationContainer
        ? ReactDOM.createPortal(pagination, props.componentContainers.paginationContainer)
        : pagination}
    </>
  );
}
